import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardText, Handshake, ChatCenteredText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SignaturePad } from "./SignaturePad";

export default function PairingsCard({ uid = "", cid = "" }) {
  const { user: me } = useAuth();
  const [pairings, setPairings] = useState(null);
  const [signing, setSigning] = useState(null);
  const [introing, setIntroing] = useState(null);
  const [feedbacking, setFeedbacking] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [sigImage, setSigImage] = useState("");
  const [adopted, setAdopted] = useState(null);
  const [consent, setConsent] = useState(false);
  const [showAlt, setShowAlt] = useState(false);
  const [introForm, setIntroForm] = useState({ happened_at: "", notes: "", is_provisional: "", shadow_or_orientation: "", shadow_details: "" });
  const [feedbackText, setFeedbackText] = useState("");
  const [busy, setBusy] = useState(false);

  const url = uid ? `/employees/${uid}/pairings` : `/clients/${cid}/pairings`;
  const load = () => api.get(url).then((r) => setPairings(r.data.pairings)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { setPairings(null); load(); }, [uid, cid]);

  const signerId = signing ? String(signing.user_id || uid) : "";
  const selfSign = !!signing && !!me?.id && signerId === String(me.id);

  useEffect(() => {
    if (!signing) return;
    setConsent(false); setShowAlt(false); setTypedName(""); setSigImage("");
    if (String(signing.user_id || uid) === String(me?.id)) {
      api.get("/my/signature").then((r) => setAdopted(r.data)).catch(() => setAdopted(null));
    } else {
      setAdopted(null);
    }
  }, [signing]);

  const sign = async (useAdopted = false) => {
    if (!consent) { toast.error("Please check the electronic signature consent box first"); return; }
    if (!useAdopted && !typedName.trim() && !sigImage) { toast.error("Sign with the pad or type your name"); return; }
    setBusy(true);
    try {
      await api.post(`/employees/${signing.user_id || uid}/care-plan-signatures`,
        { client_id: signing.client_id, typed_name: typedName, signature_image: sigImage, use_adopted: useAdopted, consent: true });
      toast.success(`Care plan signed for ${signing.client_name || signing.employee_name}`);
      setSigning(null); setTypedName(""); setSigImage("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const saveIntro = async () => {
    setBusy(true);
    try {
      await api.post(`/employees/${introing.user_id || uid}/introductions`,
        { client_id: introing.client_id, ...introForm });
      toast.success("Face-to-face documented");
      setIntroing(null); setIntroForm({ happened_at: "", notes: "", is_provisional: "", shadow_or_orientation: "", shadow_details: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const askToSign = async (p) => {
    if (!p.user_id) { toast.error("No login on file for this caregiver — record the signature here instead"); return; }
    try {
      await api.post(`/admin/employees/${p.user_id}/request-plan-signature`, { client_id: p.client_id });
      toast.success(`Sent to ${p.employee_name} — it's on their profile under Things to complete`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPacket = async (p) => {
    try {
      const r = await api.get(`/employees/${p.user_id || uid}/care-plan-signatures/${p.client_id}/packet.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveFeedback = async () => {
    setBusy(true);
    try {
      await api.put(`/introductions/${feedbacking.introduction.id}/feedback`, { feedback: feedbackText });
      toast.success("Feedback recorded");
      setFeedbacking(null); setFeedbackText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!pairings) return null;

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid={uid ? `pairings-card-${uid}` : `pairings-card-client-${cid}`}>
      <h2 className="font-heading text-lg font-semibold text-stone-900">
        {uid ? "Clients — care plan signatures & introductions" : "Caregivers — care plan signatures & introductions"}
      </h2>
      <p className="text-xs text-stone-500">
        Every {uid ? "client this person works with" : "caregiver who works with this client"} — the care plan must be signed, the face-to-face documented, feedback recorded. Visit data lands ~2 weeks late, so this is the catch-up net.
      </p>
      {pairings.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed border-stone-200 px-4 py-5 text-center text-xs text-stone-400">No pairings yet — they appear from assignments and visit data.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {pairings.map((p) => (
            <div key={p.client_id || p.user_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 px-4 py-3" data-testid={`pairing-${p.client_id || p.user_id}`}>
              <div className="min-w-40 flex-1">
                {uid ? (
                  <Link to={`/clients/${p.client_id}`} className="text-sm font-semibold text-primary hover:underline" data-testid={`pairing-client-${p.client_id}`}>{p.client_name}</Link>
                ) : (
                  <Link to={`/team/${p.user_id}`} className="text-sm font-semibold text-primary hover:underline" data-testid={`pairing-emp-${p.user_id}`}>{p.employee_name}</Link>
                )}
                {p.first_visit && <p className="text-[11px] text-stone-400">first visit on record {p.first_visit}</p>}
              </div>
              {p.no_login ? (
                <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500" data-testid={`pairing-nologin-${p.client_id}`}>no app login — office tracks on paper</span>
              ) : p.signature ? (
                <>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]" data-testid={`pairing-signed-${p.client_id}`}>
                    <ClipboardText size={11} /> plan signed {(p.signature.signed_at || "").slice(0, 10)}
                  </span>
                  {p.signature.has_packet && (
                    <button onClick={() => openPacket(p)} className="rounded-full border border-[#27AE60]/40 px-2.5 py-0.5 text-[10px] font-semibold text-[#1e8449] hover:bg-[#27AE60]/10" data-testid={`pairing-packet-${p.client_id}`}>
                      Signed plan + certificate ↗
                    </button>
                  )}
                </>
              ) : (
                <>
                  {!p.no_login && p.user_id && (
                    <button onClick={() => askToSign(p)} className="rounded-full border border-primary/40 px-3 py-1 text-[11px] font-semibold text-primary hover:bg-primary/5" data-testid={`pairing-ask-sign-${p.client_id}`}>
                      Ask to sign
                    </button>
                  )}
                  <button onClick={() => setSigning(p)} className="rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-stone-900" data-testid={`pairing-sign-${p.client_id}`}>
                    Sign care plan
                  </button>
                </>
              )}
              {!p.no_login && (p.introduction?.happened_at ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]" data-testid={`pairing-intro-done-${p.client_id}`}>
                  <Handshake size={11} /> face-to-face {p.introduction.happened_at}
                </span>
              ) : (
                <button onClick={() => setIntroing(p)} className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold text-stone-600 hover:border-primary/50" data-testid={`pairing-intro-${p.client_id}`}>
                  Document face-to-face
                </button>
              ))}
              {p.introduction?.happened_at && (p.introduction.feedback ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500" title={p.introduction.feedback} data-testid={`pairing-feedback-done-${p.client_id}`}>
                  <ChatCenteredText size={11} /> feedback ✓
                </span>
              ) : (
                <button onClick={() => setFeedbacking(p)} className="rounded-full border border-[#B7791F]/40 px-3 py-1 text-[11px] font-semibold text-[#8a5d14] hover:bg-[#B7791F]/10" data-testid={`pairing-feedback-${p.client_id}`}>
                  Record feedback
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!signing} onOpenChange={(o) => !o && setSigning(null)}>
        <DialogContent data-testid="sign-careplan-dialog">
          <DialogHeader><DialogTitle>Sign the care plan — {signing?.client_name || signing?.employee_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-stone-500">The signer confirms they reviewed the current care plan for this client before providing care. This is the signature of record — date, time, device and IP address are saved as proof.</p>
          <div className="mt-2 space-y-3">
            {signing?.client_id && (
              <Link to={selfSign ? `/my-careplan/${signing.client_id}` : `/clients/${signing.client_id}`} target="_blank"
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary underline" data-testid="sign-review-plan">
                Review the care plan first →
              </Link>
            )}
            {selfSign && adopted?.adopted && !showAlt ? (
              <div className="rounded-lg border border-primary/25 bg-primary/[0.04] p-3" data-testid="sign-adopted-block">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Your adopted signature</p>
                {adopted.pretty_png && <img src={adopted.pretty_png} alt={`Adopted signature of ${adopted.typed_name}`} className="mt-1 h-14" data-testid="sign-adopted-preview" />}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button onClick={() => sign(true)} disabled={busy || !consent}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="sign-use-adopted">
                    {busy ? "Signing…" : "Sign Document"}
                  </Button>
                  <button onClick={() => setShowAlt(true)} className="text-[11px] font-semibold text-stone-500 underline" data-testid="sign-other-toggle">
                    Sign a different way
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type full name to sign" className="border-stone-300" data-testid="sign-typed-name" />
                <p className="text-center text-[10px] uppercase tracking-wide text-stone-300">— or sign with the pad —</p>
                <div className="rounded-lg border border-stone-200">
                  <SignaturePad onChange={(img) => setSigImage(img || "")} />
                </div>
              </>
            )}
            {selfSign && adopted && !adopted.adopted && (
              <p className="text-[11px] text-stone-500" data-testid="sign-adopt-tip">
                Tip: <Link to="/profile" className="font-semibold text-primary underline">adopt your signature</Link> on My Profile and signing becomes one click — a clean, legible signature every time.
              </p>
            )}
            <label className="flex items-start gap-2.5 rounded-lg border border-stone-200 px-3 py-2.5" data-testid="sign-consent-row">
              <input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="sign-consent" />
              <span className="text-xs text-stone-600">
                {selfSign
                  ? "I have reviewed this document and intend to sign it electronically."
                  : "The signer reviewed this document and gave their signature — I am recording it on their behalf."}
              </span>
            </label>
            {(!selfSign || !adopted?.adopted || showAlt) && (
              <Button onClick={() => sign(false)} disabled={busy || !consent} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="sign-careplan-submit">
                {busy ? "Signing…" : "Sign Document"}
              </Button>
            )}
            {signing && !selfSign && (
              <p className="text-[10px] text-stone-400">Office recording on the caregiver's behalf — only record a signature they actually gave.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!introing} onOpenChange={(o) => !o && setIntroing(null)}>
        <DialogContent data-testid="intro-dialog">
          <DialogHeader><DialogTitle>Face-to-face introduction — {introing?.client_name}</DialogTitle></DialogHeader>
          <div className="mt-2 space-y-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">When did it happen?</span>
              <Input type="date" value={introForm.happened_at} onChange={(e) => setIntroForm((f) => ({ ...f, happened_at: e.target.value }))} className="mt-1 border-stone-300" data-testid="intro-date" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Is the employee provisionally hired?</span>
              <select value={introForm.is_provisional} onChange={(e) => setIntroForm((f) => ({ ...f, is_provisional: e.target.value }))} className="mt-1 h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="intro-provisional">
                <option value="">—</option><option>Yes</option><option>No</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Shadowed another employee / orientation on another day?</span>
              <select value={introForm.shadow_or_orientation} onChange={(e) => setIntroForm((f) => ({ ...f, shadow_or_orientation: e.target.value }))} className="mt-1 h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="intro-shadow">
                <option value="">—</option><option>Yes</option><option>No</option>
              </select>
            </label>
            {introForm.shadow_or_orientation === "Yes" && (
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Shadow / orientation details (who, when)</span>
                <Input value={introForm.shadow_details} onChange={(e) => setIntroForm((f) => ({ ...f, shadow_details: e.target.value }))} className="mt-1 border-stone-300" data-testid="intro-shadow-details" />
              </label>
            )}
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Notes (optional)</span>
              <textarea value={introForm.notes} onChange={(e) => setIntroForm((f) => ({ ...f, notes: e.target.value }))} rows={2}
                className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm" data-testid="intro-notes" />
            </label>
            <Button onClick={saveIntro} disabled={busy || !introForm.happened_at} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intro-save">
              {busy ? "Saving…" : "Document it"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!feedbacking} onOpenChange={(o) => !o && setFeedbacking(null)}>
        <DialogContent data-testid="feedback-dialog">
          <DialogHeader><DialogTitle>Introduction feedback — {feedbacking?.client_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-stone-500">How did it go? (client/family reaction, fit, anything to watch)</p>
          <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={3}
            className="mt-2 w-full rounded-md border border-stone-300 px-3 py-2 text-sm" data-testid="feedback-text" />
          <Button onClick={saveFeedback} disabled={busy || !feedbackText.trim()} className="mt-2 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="feedback-save">
            {busy ? "Saving…" : "Record feedback"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
